<template>
  <div class="d-inline-block">
    <v-menu
      v-model="menu"
      offset-y
      left
      :close-on-content-click="false"
      max-width="380"
      min-width="320"
      @input="onToggle"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          class="notification-bell-btn mr-1"
          :aria-label="$t('notifications')"
          v-bind="attrs"
          v-on="on"
        >
          <v-badge
            :content="unread > 99 ? '99+' : String(unread)"
            :value="unread > 0"
            color="red"
            overlap
          >
            <v-icon small>fal fa-bell</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <v-card class="notification-menu">
        <v-card-title class="subtitle-1 font-weight-bold py-2">
          {{ $t("notifications") }}
          <v-spacer />
          <v-chip v-if="unread > 0" x-small label dark color="red">
            {{ unread > 99 ? "99+" : unread }} {{ $t("notification_unread") }}
          </v-chip>
        </v-card-title>
        <v-divider />

        <div v-if="loading && !items.length" class="text-center py-6">
          <v-progress-circular indeterminate size="22" color="primary" />
        </div>
        <div v-else-if="!items.length" class="text-center grey--text py-6">
          {{ $t("notification_empty") }}
        </div>
        <v-list v-else dense class="py-0 notification-menu__list">
          <v-list-item
            v-for="item in items"
            :key="item.id"
            :class="{ 'notification-unread': !item.is_read }"
            @click="openItem(item)"
          >
            <v-list-item-avatar size="32" color="grey lighten-4" class="mr-3">
              <v-icon x-small color="primary">{{ icon(item.type) }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title :class="{ 'font-weight-bold': !item.is_read }">
                {{ text(item.title) }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                {{ (item.starts_at || item.created_at) | moment }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="!item.is_read" class="my-0">
              <v-icon x-small color="red">fas fa-circle</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-divider />
        <div class="text-center py-1">
          <v-btn text x-small color="primary" @click="viewAll">
            {{ $t("notification_view_all") }}
          </v-btn>
        </div>
      </v-card>
    </v-menu>

    <announcement-dialog v-model="dialog" :item="selected" />
  </div>
</template>

<script>
import AnnouncementDialog from "@/components/AnnouncementDialog";
import { localizedText, announcementIcon } from "@/utils/announcement";
import { myMixin } from "@/mixin/main";

export default {
  name: "NotificationBell",
  mixins: [myMixin],
  components: { AnnouncementDialog },
  data: () => ({
    menu: false,
    dialog: false,
    loading: false,
    items: [],
    unread: 0,
    selected: null,
  }),
  mounted() {
    this.load();
    // the login popup marks announcements as seen; keep the badge in sync
    this.$root.$on("announcements-updated", this.load);
  },
  beforeDestroy() {
    this.$root.$off("announcements-updated", this.load);
  },
  methods: {
    text(map) {
      return localizedText(map, this.$i18n.locale);
    },
    icon: announcementIcon,
    onToggle(open) {
      if (open) this.load();
    },
    async load() {
      try {
        this.loading = true;
        const { data } = await this.$http.get("announcements/history", {
          params: { page: 1, per_page: 10 },
        });
        if (data.status) {
          this.items = data.data.notifications.data;
          this.unread = data.data.unread_count;
        }
      } catch (_) {
        // the bell must never break the page it sits on
      } finally {
        this.loading = false;
      }
    },
    async openItem(item) {
      this.selected = item;
      this.menu = false;
      this.dialog = true;
      if (item.is_read) return;
      try {
        await this.$http.post(`announcements/${item.id}/seen`);
        item.is_read = true;
        this.unread = Math.max(0, this.unread - 1);
      } catch (_) {
        /* stays unread; will retry next open */
      }
    },
    viewAll() {
      this.menu = false;
      if (this.$route.name !== "notifications") {
        this.$router.push({ name: "notifications" });
      }
    },
  },
};
</script>

<style scoped>
/* same look as the layouts' icon buttons (theme toggle / drawer toggle) */
.notification-bell-btn {
  width: 32px !important;
  height: 32px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  border-radius: 8px !important;
  color: #94a3b8;
  transition: color 0.15s ease, border-color 0.15s ease,
    background-color 0.15s ease;
}

.notification-bell-btn:hover {
  color: #5b39c9 !important;
  border-color: #5b39c9 !important;
  background: #f3f0fc !important;
}

.theme--dark .notification-bell-btn {
  background: transparent;
  border-color: #2f333a !important;
  color: rgba(226, 232, 240, 0.6);
}

.theme--dark .notification-bell-btn:hover {
  color: #7c5cea !important;
  border-color: #7c5cea !important;
  background: rgba(91, 57, 201, 0.2) !important;
}

.notification-menu__list {
  max-height: 420px;
  overflow-y: auto;
}
.notification-unread {
  background: rgba(91, 57, 201, 0.06);
}
</style>
